/* eslint-disable @typescript-eslint/no-explicit-any */
// lib/firebaseClient.ts
import { initializeApp, getApps } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getAI, getGenerativeModel, GoogleAIBackend } from "firebase/ai";


interface FirebaseClientConfig {
  apiKey: string;
  authDomain: string;
  databaseURL: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId: string;
}

class FirebaseConfigService {
  private secrets: Record<string, string> | null = null;
  private initializationPromise: Promise<Record<string, string> | null> | null = null;

  /**
   * Load secrets - first try from process.env, then AWS
   */
  async loadSecrets(): Promise<Record<string, string> | null> {
    if (this.secrets) {
      return this.secrets;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this._loadSecretsInternal();
    this.secrets = await this.initializationPromise;

    return this.secrets;
  }

  private async _loadSecretsInternal(): Promise<Record<string, string> | null> {
    try {
      // First check if secrets are already in process.env
      if (this._hasRequiredEnvVars()) {
        console.log('[FirebaseConfigService] Using existing environment variables');
        return this._getSecretsFromEnv();
      }

      // Try to initialize secrets via API call
      console.log('[FirebaseConfigService] Loading secrets via API...');
      const response = await fetch('/api/init-secrets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Failed to load secrets: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to load secrets');
      }

      console.log(`[FirebaseConfigService] Successfully loaded ${result.secretsLoaded} secrets`);

      // After API call, secrets should be in process.env, but that's server-side
      // For client-side, we need to get them via another API call
      return await this._fetchSecretsFromAPI();

    } catch (error: any) {
      console.error(`[FirebaseConfigService] Error loading secrets: ${error.message}`);
      throw error;
    }
  }

  private _hasRequiredEnvVars(): boolean {
    const required = [
      'FIREBASE_CLIENT_API_KEY',
      'FIREBASE_CLIENT_AUTH_DOMAIN',
      'FIREBASE_CLIENT_PROJECT_ID',
      'FIREBASE_CLIENT_STORAGE_BUCKET',
      'FIREBASE_CLIENT_MESSAGING_SENDER_ID',
      'FIREBASE_CLIENT_APP_ID'
    ];

    // In Next.js, check both process.env and NEXT_PUBLIC_ prefixed vars
    const hasEnvVars = required.every(key => {
      const value = process.env[key] || process.env[`NEXT_PUBLIC_${key}`];
      return !!value;
    });

    console.log('[FirebaseConfigService] Environment vars check:', {
      hasEnvVars,
      availableKeys: Object.keys(process.env).filter(k => k.includes('FIREBASE'))
    });

    return hasEnvVars;
  }

  private _getSecretsFromEnv(): Record<string, string> {
    return {
      FIREBASE_CLIENT_API_KEY: process.env.FIREBASE_CLIENT_API_KEY || '',
      FIREBASE_CLIENT_AUTH_DOMAIN: process.env.FIREBASE_CLIENT_AUTH_DOMAIN || '',
      FIREBASE_CLIENT_DATABASE_URL: process.env.FIREBASE_CLIENT_DATABASE_URL || '',
      FIREBASE_CLIENT_PROJECT_ID: process.env.FIREBASE_CLIENT_PROJECT_ID || '',
      FIREBASE_CLIENT_STORAGE_BUCKET: process.env.FIREBASE_CLIENT_STORAGE_BUCKET || '',
      FIREBASE_CLIENT_MESSAGING_SENDER_ID: process.env.FIREBASE_CLIENT_MESSAGING_SENDER_ID || '',
      FIREBASE_CLIENT_APP_ID: process.env.FIREBASE_CLIENT_APP_ID || '',
      FIREBASE_CLIENT_MEASUREMENT_ID: process.env.FIREBASE_CLIENT_MEASUREMENT_ID || ''
    };
  }

  private async _fetchSecretsFromAPI(): Promise<Record<string, string>> {
    console.log('[FirebaseConfigService] Fetching config from API...');

    const response = await fetch('/api/get-firebase-config', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch Firebase config: ${response.status}`);
    }

    const config = await response.json();

    console.log('[FirebaseConfigService] Raw API response:');
    console.log('  - Raw apiKey present:', !!config.apiKey);
    console.log('  - Raw apiKey length:', config.apiKey?.length || 0);
    console.log('  - Raw config keys:', Object.keys(config));

    // The API now returns Firebase-compatible property names
    // Convert them back to our internal naming convention for consistency
    const internalConfig = {
      FIREBASE_CLIENT_API_KEY: config.apiKey,
      FIREBASE_CLIENT_AUTH_DOMAIN: config.authDomain,
      FIREBASE_CLIENT_DATABASE_URL: config.databaseURL,
      FIREBASE_CLIENT_PROJECT_ID: config.projectId,
      FIREBASE_CLIENT_STORAGE_BUCKET: config.storageBucket,
      FIREBASE_CLIENT_MESSAGING_SENDER_ID: config.messagingSenderId,
      FIREBASE_CLIENT_APP_ID: config.appId,
      FIREBASE_CLIENT_MEASUREMENT_ID: config.measurementId
    };

    console.log('[FirebaseConfigService] Converted to internal format:');
    console.log('  - FIREBASE_CLIENT_API_KEY present:', !!internalConfig.FIREBASE_CLIENT_API_KEY);
    console.log('  - Internal config keys:', Object.keys(internalConfig));

    return internalConfig;
  }

  async getFirebaseClientConfig(): Promise<FirebaseClientConfig> {
    const secrets = await this.loadSecrets();

    if (!secrets) {
      throw new Error('Failed to load Firebase configuration secrets');
    }

    console.log('[FirebaseConfigService] Config received from API...');

    // Use the correct internal property names that _fetchSecretsFromAPI returns
    const apiKey = secrets.FIREBASE_CLIENT_API_KEY;
    const authDomain = secrets.FIREBASE_CLIENT_AUTH_DOMAIN;
    const databaseURL = secrets.FIREBASE_CLIENT_DATABASE_URL;
    const projectId = secrets.FIREBASE_CLIENT_PROJECT_ID;
    const storageBucket = secrets.FIREBASE_CLIENT_STORAGE_BUCKET;
    const messagingSenderId = secrets.FIREBASE_CLIENT_MESSAGING_SENDER_ID;
    const appId = secrets.FIREBASE_CLIENT_APP_ID;
    const measurementId = secrets.FIREBASE_CLIENT_MEASUREMENT_ID;

    console.log('  - apiKey present:', !!apiKey);
    console.log('  - apiKey length:', apiKey?.length || 0);
    console.log('  - authDomain present:', !!authDomain);
    console.log('  - projectId present:', !!projectId);

    if (apiKey) {
      console.log('  - apiKey starts with:', apiKey.substring(0, 10) + '...');
    }

    // Validate required keys
    const requiredKeys = [
      { name: 'apiKey', value: apiKey },
      { name: 'authDomain', value: authDomain },
      { name: 'projectId', value: projectId },
      { name: 'storageBucket', value: storageBucket },
      { name: 'messagingSenderId', value: messagingSenderId },
      { name: 'appId', value: appId }
    ];

    const missingKeys = requiredKeys.filter(key => !key.value);
    if (missingKeys.length > 0) {
      console.error('[FirebaseConfigService] Missing keys:', missingKeys.map(k => k.name));
      throw new Error(`Missing required Firebase configuration keys: ${missingKeys.map(k => k.name).join(', ')}`);
    }

    // Validate API key format
    if (!apiKey || apiKey.length < 30 || !apiKey.startsWith('AIza')) {
      throw new Error(`Invalid Firebase API key format: ${apiKey ? 'incorrect format' : 'missing'}`);
    }

    console.log('[FirebaseConfigService] All Firebase config validated successfully');

    // Return the config in the expected FirebaseClientConfig format
    return {
      apiKey: apiKey,
      authDomain: authDomain,
      databaseURL: databaseURL || '',
      projectId: projectId,
      storageBucket: storageBucket,
      messagingSenderId: messagingSenderId,
      appId: appId,
      measurementId: measurementId || ''
    };
  }
}

// Firebase instances
let firebaseApp: any = null;
let firebaseDb: any = null;
let firebaseAuth: any = null;
let firebaseStorage: any = null;
let initializationPromise: Promise<any> | null = null;
let firebaseAI: any = null;
let firebaseModel: any = null;

const configService = new FirebaseConfigService();

async function initializeFirebase(): Promise<{ app: any; db: any; auth: any; storage: any; ai: any; model: any }> {
  if (firebaseApp && firebaseDb && firebaseAuth && firebaseStorage && firebaseAI && firebaseModel) {
    return { app: firebaseApp, db: firebaseDb, auth: firebaseAuth, storage: firebaseStorage, ai: firebaseAI, model: firebaseModel };
  }

  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = _initializeFirebaseInternal();
  return initializationPromise;
}

async function _initializeFirebaseInternal(): Promise<{ app: any; db: any; auth: any; storage: any; ai: any; model: any }> {
  try {
    console.log('[FirebaseClient] Initializing Firebase...');

    const firebaseConfig = await configService.getFirebaseClientConfig();
    console.log('[FirebaseClient] Config received, API key length:', firebaseConfig.apiKey?.length || 0);

    if (!firebaseConfig.apiKey || firebaseConfig.apiKey.length < 30) {
      throw new Error(`Invalid Firebase API key: ${firebaseConfig.apiKey ? 'too short' : 'missing'}`);
    }

    console.log('[FirebaseClient] Config validated, initializing app...');

    // Check if app already exists, if not initialize it
    const existingApps = getApps();
    if (existingApps.length === 0) {
      console.log('🔥 Initializing new Firebase app...');
      firebaseApp = initializeApp(firebaseConfig);
      console.log('✅ Firebase app initialized successfully');
    } else {
      console.log('🔥 Using existing Firebase app');
      firebaseApp = existingApps[0];
    }

    // Initialize services
    firebaseDb = getFirestore(firebaseApp);
    firebaseAuth = getAuth(firebaseApp);
    firebaseStorage = getStorage(firebaseApp);

    // Initialize the Gemini Developer API backend service
    firebaseAI = getAI(firebaseApp, { backend: new GoogleAIBackend() });

    // Create a `GenerativeModel` instance with a model that supports your use case
    firebaseModel = getGenerativeModel(firebaseAI, { model: "gemini-2.5-flash" });

    console.log('[FirebaseClient] All Firebase services initialized successfully');
    return { app: firebaseApp, db: firebaseDb, auth: firebaseAuth, storage: firebaseStorage, ai: firebaseAI, model: firebaseModel };
  } catch (error) {
    console.error('[FirebaseClient] Failed to initialize Firebase:', error);
    initializationPromise = null;
    throw error;
  }
}

export async function getFirebaseServices(): Promise<{ app: any; db: any; auth: any; storage: any }> {
  return initializeFirebase();
}

export async function getFirebaseDB(): Promise<any> {
  const { db } = await initializeFirebase();
  return db;
}

export async function getFirebaseAuth(): Promise<any> {
  const { auth } = await initializeFirebase();
  return auth;
}

export async function getFirebaseStorage(): Promise<any> {
  const { storage } = await initializeFirebase();
  return storage;
}

export async function getFirebaseApp(): Promise<any> {
  const { app } = await initializeFirebase();
  return app;
}

export async function getFirebaseAI(): Promise<any> {
  const { ai } = await initializeFirebase();
  return ai;
}

export async function getFirebaseModel(): Promise<any> {
  const { model } = await initializeFirebase();
  return model;
}

export { configService };

// Legacy exports (undefined - use async functions above)
export const db = undefined;
export const auth = undefined;
export const storage = undefined;
export const app = undefined;
export default undefined;